import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { WheelTimePicker } from "@src/appV2/redesign/components/WheelTimePicker/WheelTimePicker";
import { CbhIcon, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { getEffectiveGeofenceArrival } from "@src/appV2/redesign/WorkerShifts/MissedPunches/utils/getEffectiveGeofenceArrival";
import { isAfter } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { useClockInTimeValidation } from "../../Timekeeping/hooks/useClockInTimeValidation";
import {
  getBottomSheetIntent,
  TimekeepingAdjustmentBottomSheet,
} from "../../Timekeeping/TimekeepingAdjustmentBottomSheet";
import { getDayIndicator } from "../../Timekeeping/Timesheet/utils/getDayIndicator";
import { inferDateFromTime } from "../../Timekeeping/Timesheet/utils/inferDateFromTime";
import {
  getLocalTimeForPicker,
  getUtcTimeFromPicker,
} from "../../Timekeeping/Timesheet/utils/timezoneConversion";

export interface AlreadyStartedWorkingBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  shiftStartAt: Date;
  shiftEndAt: Date;
  workplaceTimezone: string;
  geofenceArrivalTime?: Date;
  earliestPlausibleArrivalTime?: Date;
  workplaceSettings: {
    preventEarlyClockIns: boolean;
    earlyClockInMinutes?: number;
  };
  isEnforceGeofenceTimestampsEnabled: boolean;
  isGeofenceWorkplace?: boolean;
  isNfcOrMeshWorkplace?: boolean;
  existingReason?: string;
  existingStart?: Date;
  onConfirm: (selectedTime: Date, reason: string) => void | Promise<void>;
  onCancel?: () => void;
}

const INFO_BANNER_TEXT =
  "You are requesting an earlier clock in. No worries - you can still receive some of your shift pay via InstantPay.";

export function AlreadyStartedWorkingBottomSheet(props: AlreadyStartedWorkingBottomSheetProps) {
  const {
    modalState,
    shiftId,
    shiftStartAt,
    shiftEndAt,
    workplaceTimezone,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    workplaceSettings,
    isEnforceGeofenceTimestampsEnabled,
    isGeofenceWorkplace,
    isNfcOrMeshWorkplace,
    existingReason,
    existingStart,
    onConfirm,
    onCancel,
  } = props;

  const [isPickerScrolling, setIsPickerScrolling] = useState(false);

  const prefillTime = useMemo(() => {
    if (isDefined(existingStart)) {
      return existingStart;
    }

    const { value: arrivalTime } = getEffectiveGeofenceArrival(
      geofenceArrivalTime,
      earliestPlausibleArrivalTime,
      isEnforceGeofenceTimestampsEnabled
    );

    if (isDefined(arrivalTime) && isAfter(arrivalTime, shiftStartAt)) {
      return arrivalTime;
    }

    return shiftStartAt;
  }, [
    existingStart,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    isEnforceGeofenceTimestampsEnabled,
    shiftStartAt,
  ]);

  const { setValue, watch, reset } = useForm<{ time?: Date }>({
    defaultValues: {
      time: getLocalTimeForPicker(prefillTime, workplaceTimezone),
    },
  });

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      reset({ time: getLocalTimeForPicker(prefillTime, workplaceTimezone) });
    }
  }, [modalState.modalIsOpen, prefillTime, reset, workplaceTimezone]);

  const currentLocalTime = watch("time");
  const currentTime = isDefined(currentLocalTime)
    ? getUtcTimeFromPicker(currentLocalTime, workplaceTimezone)
    : undefined;

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      const inferResult = inferDateFromTime({
        timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
        shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
        shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
        earlyBufferMinutes: 30,
      });
      setValue("time", inferResult.success ? inferResult.date : newTime, { shouldDirty: true });
    }
  };

  const validationResult = useClockInTimeValidation({
    selectedTime: currentTime,
    shiftId,
    shiftStartAt,
    shiftEndAt,
    workplaceTimezone,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    workplaceSettings,
    isEnforceGeofenceTimestampsEnabled,
    isGeofenceWorkplace,
    isNfcOrMeshWorkplace,
    suppressMissedPunchWarning: true,
    preventCurrentTime: true,
  });

  const prevValidationErrorRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!validationResult.isValid && isDefined(validationResult.intentContent)) {
      const errorText = String(validationResult.intentContent);
      if (prevValidationErrorRef.current !== errorText) {
        prevValidationErrorRef.current = errorText;
        logEvent(APP_V2_USER_EVENTS.ALREADY_STARTED_WORKING_VALIDATION_ERROR, {
          shiftId,
          error: errorText,
        });
      }
    } else {
      prevValidationErrorRef.current = undefined;
    }
  }, [validationResult.isValid, validationResult.intentContent, shiftId]);

  // Show info banner when there's no error from validation
  const hasValidationError =
    isDefined(validationResult.intent) && isDefined(validationResult.intentContent);
  const infoBannerProps = !hasValidationError
    ? {
        ...getBottomSheetIntent("info"),
        intentContent: INFO_BANNER_TEXT,
      }
    : {};

  const intentProps = hasValidationError
    ? {
        intent: validationResult.intent,
        intentIcon: validationResult.intentIcon,
        intentContent: validationResult.intentContent,
      }
    : infoBannerProps;

  const dayIndicator = getDayIndicator({
    selectedTimestamp: currentTime,
    referenceTimestamp: shiftStartAt,
    workplaceTimezone,
  });

  const shiftStartDisplayTime = formatTimeWithTimezone(shiftStartAt, workplaceTimezone, false);

  return (
    <TimekeepingAdjustmentBottomSheet
      requiresApproval
      modalState={modalState}
      {...intentProps}
      pickerTitle="When did you start working?"
      hasChanges={false}
      isPickerScrolling={isPickerScrolling}
      isPickerValid={validationResult.isValid && !validationResult.isValidatingOverlap}
      requiresChange={false}
      reasonTitle="Why did you miss clocking in?"
      reasonCtaLabel="Clock in now"
      reasonPlaceholder="Let the workplace know why you missed clocking in"
      reasonHelperText="Edit your message to the workplace to explain why you missed clock in."
      existingReason={existingReason}
      onPickerConfirm={async () => {
        const isValid = await validationResult.validateOverlap();
        if (isValid && isDefined(currentTime)) {
          logEvent(APP_V2_USER_EVENTS.ALREADY_STARTED_WORKING_TIME_SELECTED, {
            shiftId,
            selectedTime: currentTime.toISOString(),
            workplaceTimezone,
          });
        }
        return isValid;
      }}
      onConfirm={async (reason) => {
        if (isDefined(currentTime)) {
          await onConfirm(currentTime, reason);
        }
      }}
      onCancel={onCancel}
    >
      <Stack direction="row" gap={2} alignItems="center" width="100%">
        <Box
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <CbhIcon size="small" type="clock" />
        </Box>
        <Text bold variant="body2" sx={{ flex: 1 }}>
          Shift start
        </Text>
        <Button
          variant="outlined"
          size="small"
          sx={{
            flexShrink: 0,
            marginLeft: 2,
            backgroundColor: (theme) => theme.palette.common.white,
            pointerEvents: "none",
          }}
        >
          {shiftStartDisplayTime}
        </Button>
      </Stack>

      <WheelTimePicker
        preventParentTouchStart
        value={currentLocalTime}
        dayIndicator={dayIndicator}
        containerSx={{ alignSelf: "center" }}
        onChange={handleTimeChange}
        onScrollingChange={setIsPickerScrolling}
      />
    </TimekeepingAdjustmentBottomSheet>
  );
}
